import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, ChevronDown } from "lucide-react";
import { useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import GandaraLogo from "@/components/GandaraLogo";

export const Navigation = () => {
  const { language, translations } = useLanguage();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const t = translations;

  const getLocalizedPath = (path: string) => {
    const paths: Record<string, Record<string, string>> = {
      '/about': {
        es: '/quienes-somos',
        en: '/about',
        pt: '/sobre-nos',
        fr: '/a-propos'
      },
      '/montessori': {
        es: '/metodologia-montessori',
        en: '/montessori-education',
        pt: '/metodologia-montessori',
        fr: '/methode-montessori'
      },
      '/trilingual': {
        es: '/educacion-trilingue',
        en: '/trilingual-education',
        pt: '/educacao-trilingue',
        fr: '/education-trilingue'
      },
      '/ciencias': {
        es: '/ciencias',
        en: '/science',
        pt: '/ciencias',
        fr: '/sciences'
      },
      '/imagenes': {
        es: '/gandara-en-imagenes',
        en: '/gandara-in-pictures',
        pt: '/gandara-em-imagens',
        fr: '/gandara-en-images'
      },
      '/calendar': {
        es: '/calendario-escolar',
        en: '/school-calendar',
        pt: '/calendario-escolar',
        fr: '/calendrier-scolaire'
      },
      '/extracurriculars': {
        es: '/extraescolares',
        en: '/extracurriculars',
        pt: '/extracurriculares',
        fr: '/extrascolaires'
      },
      '/services': {
        es: '/servicios',
        en: '/services',
        pt: '/servicos',
        fr: '/services'
      },
      '/admissions': {
        es: '/admisiones',
        en: '/admissions',
        pt: '/admissoes',
        fr: '/admissions'
      },
      '/prices': {
        es: '/precios',
        en: '/prices',
        pt: '/precos',
        fr: '/tarifs'
      },
      '/open-house-registration': {
        es: '/inscripcion-puertas-abiertas',
        en: '/open-house-registration',
        pt: '/inscricao-portas-abertas',
        fr: '/inscription-portes-ouvertes'
      },
      '/birthdays': {
        es: '/cumpleanos',
        en: '/birthdays',
        pt: '/aniversarios',
        fr: '/anniversaires'
      },
      '/easter-camp': {
        es: '/campamento-semana-santa',
        en: '/easter-camp',
        pt: '/acampamento-pascoa',
        fr: '/camp-paques'
      },
      '/summer-camp': {
        es: '/campamento-verano',
        en: '/summer-camp',
        pt: '/acampamento-verao',
        fr: '/camp-ete'
      },
      '/christmas-camp': {
        es: '/campamento-navidad',
        en: '/christmas-camp',
        pt: '/acampamento-natal',
        fr: '/camp-noel'
      },
      '/courses': {
        es: '/cursos',
        en: '/courses',
        pt: '/cursos',
        fr: '/cours'
      },
    };
    return paths[path]?.[language] || path;
  };

  const handleNavigation = (path: string) => {
    navigate(`/${language}${getLocalizedPath(path)}`);
    setIsOpen(false);
  };

  const handleBookVisit = () => {
    openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language]);
    setIsOpen(false);
  };

  // Navigation labels by language
  const navLabels = {
    discover: {
      es: 'Descubre Gándara',
      en: 'Discover Gándara',
      pt: 'Descubra Gándara',
      fr: 'Découvrir Gándara'
    },
    more: {
      es: 'Más',
      en: 'More',
      pt: 'Mais',
      fr: 'Plus'
    },
    camps: {
      es: 'Campamentos',
      en: 'Camps',
      pt: 'Acampamentos',
      fr: 'Camps'
    },
    science: {
      es: 'Ciencias',
      en: 'Science',
      pt: 'Ciências',
      fr: 'Sciences'
    },
    pictures: {
      es: 'Gándara en imágenes',
      en: 'Gándara in pictures',
      pt: 'Gándara em imagens',
      fr: 'Gándara en images'
    }
  };

  // Show minimal navigation while translations load
  if (!t) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border shadow-sm">
        <nav className="container mx-auto px-6 py-3 flex justify-between items-center">
          <button
            type="button"
            aria-label="Gándara International School"
            className="cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => navigate(`/${language}`)}
          >
            <GandaraLogo />
          </button>
          <LanguageSwitcher />
        </nav>
      </header>
    );
  }

  const NavLinks = () => (
    <>
      {/* Dropdown 1: Descubre Gándara */}
      <DropdownMenu>
        <DropdownMenuTrigger className="text-foreground hover:text-primary hover:bg-muted transition-all duration-300 text-base font-medium px-5 py-2.5 rounded-full flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20">
          {navLabels.discover[language]}
          <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-popover/95 backdrop-blur-sm border-border shadow-xl rounded-xl min-w-[220px] p-2 animate-in fade-in-0 slide-in-from-top-2 duration-200">
          <DropdownMenuItem
            onClick={() => handleNavigation('/about')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {t.navigation.school.aboutUs}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleNavigation('/montessori')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {{ es: 'Método Gándara', en: 'The Gándara Method', pt: 'Método Gándara', fr: 'Méthode Gándara' }[language]}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleNavigation('/trilingual')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {t.navigation.school.trilingual}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleNavigation('/ciencias')}
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {navLabels.science[language]}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleNavigation('/imagenes')}
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {navLabels.pictures[language]}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dropdown 2: Admisiones (Destacado) */}
      <DropdownMenu>
        <DropdownMenuTrigger className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm hover:shadow-md transition-all duration-300 text-base font-semibold px-6 py-2.5 rounded-full flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-accent/30">
          {t.navigation.admissions.main}
          <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-popover/95 backdrop-blur-sm border-border shadow-xl rounded-xl min-w-[220px] p-2 animate-in fade-in-0 slide-in-from-top-2 duration-200">
          <DropdownMenuItem
            onClick={() => handleNavigation('/open-house-registration')}
            className="px-4 py-3 text-primary hover:bg-accent hover:text-accent-foreground cursor-pointer rounded-lg transition-all duration-200 font-semibold"
          >
            {t.navigation.admissions.openHouse}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => handleNavigation('/admissions')}
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-accent/10 cursor-pointer rounded-lg transition-all duration-200 focus:bg-accent/10 focus:text-primary font-medium"
          >
            {t.navigation.admissions.process}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleNavigation('/prices')}
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-accent/10 cursor-pointer rounded-lg transition-all duration-200 focus:bg-accent/10 focus:text-primary font-medium"
          >
            {t.navigation.admissions.prices}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleBookVisit}
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {t.navigation.admissions.visit}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dropdown 3: Más */}
      <DropdownMenu>
        <DropdownMenuTrigger className="text-foreground hover:text-primary hover:bg-muted transition-all duration-300 text-base font-medium px-5 py-2.5 rounded-full flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20">
          {navLabels.more[language]}
          <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-popover/95 backdrop-blur-sm border-border shadow-xl rounded-xl min-w-[220px] p-2 animate-in fade-in-0 slide-in-from-top-2 duration-200">
          <DropdownMenuItem 
            onClick={() => handleNavigation('/calendar')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {t.navigation.schoolLife.calendar}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleNavigation('/services')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {t.navigation.schoolLife.services}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleNavigation('/extracurriculars')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {t.navigation.schoolLife.extracurriculars}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {navLabels.camps[language]}
          </div>
          <DropdownMenuItem 
            onClick={() => handleNavigation('/easter-camp')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {t.navigation.activities.easterCamp}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleNavigation('/summer-camp')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {t.navigation.activities.summerCamp}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleNavigation('/christmas-camp')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            🎄 {t.navigation.activities.christmasCamp}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={() => handleNavigation('/birthdays')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            🎂 {t.navigation.activities.birthdays}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleNavigation('/courses')} 
            className="px-4 py-3 text-foreground hover:text-primary hover:bg-muted cursor-pointer rounded-lg transition-all duration-200 focus:bg-muted focus:text-primary"
          >
            {t.navigation.activities.courses}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border shadow-sm">
      <nav className="container mx-auto px-6 py-3 flex justify-between items-center">
        <button
          type="button"
          aria-label="Gándara International School"
          className="cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => navigate(`/${language}`)}
        >
          <GandaraLogo />
        </button>

        <div className="hidden md:flex items-center gap-4">
          <NavLinks />
          <LanguageSwitcher />
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <LanguageSwitcher />
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <button className="p-2 hover:bg-muted rounded-lg transition-colors duration-200">
                <Menu className="h-6 w-6 text-foreground" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[350px] bg-card border-border">
              <div className="flex flex-col gap-3 mt-8">
                <NavLinks />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
